import { Fragment, useEffect, useMemo, useState } from 'react';
import FilterOptions from './components/FilterOptions';
import { getApiErrorMessage } from '../../api';
import { vehicleService, type Vehicle, type VehicleStatus } from '../../api/vehicles';
import { useAuth } from '../../context/AuthContext';
import {
  Car,
  Search,
  RefreshCw,
  Filter,
  Fuel,
  Gauge,
  Building2,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';
import {
  PageHeader,
  Button,
  Badge,
  Input,
  SectionCard,
  CardHeader,
  CardBody,
  ModernTable,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from '../../components/ui/SharedLayout';

interface Veiculo {
  id: number;
  placa: string;
  modelo: string;
  marca: string;
  ano: number;
  tipoCombustivel: string;
  orgaoResponsavel: string;
  quilometragemAtual: number;
  status: VehicleStatus;
}

const mapVehicle = (vehicle: Vehicle): Veiculo => ({
  id: vehicle.id,
  placa: vehicle.license_plate,
  modelo: vehicle.description,
  marca: vehicle.brand,
  ano: vehicle.year,
  tipoCombustivel: vehicle.fuel || 'Não informado',
  orgaoResponsavel: vehicle.department_name || '-',
  quilometragemAtual: vehicle.mileage || 0,
  status: vehicle.status || 'disponivel',
});

const renderStatusBadge = (status: VehicleStatus) => {
  switch (status) {
    case 'disponivel':
      return <Badge tone="emerald">Disponível</Badge>;
    case 'manutencao':
      return <Badge tone="amber">Em manutenção</Badge>;
    case 'em_uso':
      return <Badge tone="blue">Em uso</Badge>;
    default:
      return <Badge tone="slate">{String(status)}</Badge>;
  }
};

const ITEMS_PER_PAGE = 20;

const CadastroVeiculos = () => {
  const { setPageTitle } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [veiculos, setVeiculos] = useState<Veiculo[]>([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<Record<string, string[]>>({});
  const [filterModalKey, setFilterModalKey] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setPageTitle('Veículos do meu escopo');
  }, [setPageTitle]);

  const filterCategories = {
    Status: [
      { id: 'disponivel', label: 'Disponível' },
      { id: 'manutencao', label: 'Em Manutenção' },
      { id: 'em_uso', label: 'Em Uso' },
    ],
    Combustível: [
      { id: 'Flex', label: 'Flex' },
      { id: 'Gasolina', label: 'Gasolina' },
      { id: 'Diesel', label: 'Diesel' },
    ],
  };

  const carregarVeiculos = async () => {
    setLoading(true);
    setError(null);
    try {
      const vehicles = await vehicleService.getAllVehicles();
      setVeiculos((vehicles || []).map(mapVehicle));
    } catch (err) {
      setVeiculos([]);
      setError(getApiErrorMessage(err, 'Erro ao carregar veículos do seu escopo.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void carregarVeiculos();
  }, []);

  const filteredVeiculos = useMemo(() => {
    let filtered = [...veiculos];

    const term = searchTerm.trim().toLowerCase();
    if (term) {
      filtered = filtered.filter((veiculo) =>
        [
          veiculo.placa,
          veiculo.modelo,
          veiculo.marca,
          veiculo.tipoCombustivel,
          veiculo.orgaoResponsavel,
          veiculo.status,
          String(veiculo.ano),
          String(veiculo.quilometragemAtual),
        ]
          .join(' ')
          .toLowerCase()
          .includes(term),
      );
    }

    if (Object.keys(appliedFilters).length > 0) {
      Object.entries(appliedFilters).forEach(([category, values]) => {
        if (values.length > 0) {
          filtered = filtered.filter((veiculo) => {
            if (category === 'Status') {
              return values.includes(veiculo.status);
            } else if (category === 'Combustível') {
              return values.includes(veiculo.tipoCombustivel);
            }
            return true;
          });
        }
      });
    }

    return filtered;
  }, [searchTerm, appliedFilters, veiculos]);

  const totalPages = Math.max(1, Math.ceil(filteredVeiculos.length / ITEMS_PER_PAGE));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [filteredVeiculos.length, currentPage, totalPages]);

  const { paginatedVeiculos, pageRange } = useMemo(() => {
    const total = filteredVeiculos.length;
    const safePage = Math.min(currentPage, totalPages);
    const startIndex = (safePage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const sliced = filteredVeiculos.slice(startIndex, endIndex);
    return {
      paginatedVeiculos: sliced,
      pageRange: {
        start: total === 0 ? 0 : startIndex + 1,
        end: Math.min(endIndex, total),
        total,
      },
    };
  }, [filteredVeiculos, currentPage, totalPages]);

  const getPageNumbers = useMemo(() => {
    const pages: (number | '...')[] = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible + 2) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    const left = Math.max(2, currentPage - 1);
    const right = Math.min(totalPages - 1, currentPage + 1);
    if (left > 2) pages.push('...');
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages - 1) pages.push('...');
    pages.push(totalPages);
    return pages;
  }, [currentPage, totalPages]);

  const handleApplyFilter = (selectedFilters: Record<string, string[]>) => {
    setAppliedFilters(selectedFilters);
  };

  const handleOpenFilter = () => {
    setFilterModalKey((k) => k + 1);
    setIsFilterOpen(true);
  };

  const handleClearFilters = () => {
    setAppliedFilters({});
  };

  const appliedFiltersCount = useMemo(() => {
    return Object.values(appliedFilters).reduce(
      (total, values) => total + values.length,
      0,
    );
  }, [appliedFilters]);

  return (
    <div className="w-full">
      <div className="mx-auto w-full max-w-[1440px] px-4 sm:px-6 lg:px-8 py-7">
        <PageHeader title="Veículos do meu escopo" hideTitleInBody />

        <div className="mb-7 flex flex-col gap-1.5 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1.5 max-w-2xl">
            <p className="text-[15px] font-semibold leading-tight text-sky-600">
              Gestão de veículos
            </p>
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <h1 className="text-[22px] sm:text-[26px] font-bold tracking-tight leading-tight text-slate-900">
                Veículos liberados
              </h1>
              <span className="text-[18px] sm:text-[20px] font-medium text-slate-500 leading-tight">
                para o seu acesso
              </span>
            </div>
            <p className="mt-2 text-[13px] font-medium text-slate-500 max-w-xl">
              Esta lista utiliza apenas veículos liberados pelo seu escopo de acesso.
            </p>
          </div>
          <div className="flex flex-col-reverse sm:flex-row sm:items-center gap-2 w-full sm:w-auto mt-1 sm:mt-0">
            {appliedFiltersCount > 0 && (
              <Button variant="secondary" onClick={handleClearFilters} className="h-10 px-4">
                <X className="h-4 w-4 stroke-[1.8]" />
                Limpar filtros
              </Button>
            )}
            <Button
              variant={appliedFiltersCount > 0 ? 'primary' : 'secondary'}
              onClick={handleOpenFilter}
              className="h-10 px-4"
            >
              <Filter className="h-4 w-4 stroke-[1.9]" />
              Filtros
              {appliedFiltersCount > 0 && (
                <span className="ml-0.5 inline-flex items-center justify-center min-w-[20px] h-5 rounded-full bg-sky-600 text-white text-[11px] font-bold px-1.5">
                  {appliedFiltersCount}
                </span>
              )}
            </Button>
            <Button variant="secondary" onClick={() => void carregarVeiculos()} className="h-10 px-4">
              <RefreshCw className="h-4 w-4 stroke-[1.8]" />
              Atualizar
            </Button>
          </div>
        </div>

        {error && (
          <div className="mb-5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
            {error}
          </div>
        )}

        <SectionCard>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-gradient-to-br from-sky-500 via-indigo-500 to-blue-700 text-white shadow-[0_6px_16px_-8px_rgba(59,130,246,0.6)]">
                <Car className="h-[18px] w-[18px] stroke-[1.9]" />
              </div>
              <div>
                <h2 className="text-[15px] font-bold tracking-tight text-slate-900">
                  Meus Veículos
                </h2>
                <p className="text-[13px] text-slate-500 mt-0.5">
                  {loading
                    ? 'Carregando veículos...'
                    : pageRange.total === 0
                      ? 'Nenhum veículo disponível no seu escopo atual.'
                      : `${pageRange.start} a ${pageRange.end} de ${pageRange.total} veículos listados (total no escopo: ${veiculos.length})`}
                </p>
              </div>
            </div>
            <div className="w-full sm:w-[360px] shrink-0 relative">
              <Search
                className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 stroke-[1.8] text-slate-400"
                aria-hidden="true"
              />
              <Input
                type="text"
                name="search-vehicles"
                placeholder="Pesquisar placa, marca, modelo, ano, departamento..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="!pl-10"
              />
            </div>
          </CardHeader>

          <CardBody className="!py-3">
            {loading ? (
              <div className="flex justify-center items-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-sm font-medium text-slate-600">
                  Carregando veículos...
                </span>
              </div>
            ) : (
              <Fragment>
                <ModernTable>
                  <Thead>
                    <Tr>
                      <Th>Veículo</Th>
                      <Th>Marca</Th>
                      <Th>Ano</Th>
                      <Th>Combustível</Th>
                      <Th>Quilometragem</Th>
                      <Th>Departamento</Th>
                      <Th>Status</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {pageRange.total === 0 ? (
                      <tr>
                        <td
                          colSpan={7}
                          className="px-5 py-14 text-center text-sm text-slate-500"
                        >
                          Nenhum veículo disponível no seu escopo atual.
                        </td>
                      </tr>
                    ) : (
                      paginatedVeiculos.map((veiculo) => (
                        <Tr key={veiculo.id}>
                          <Td>
                            <div className="flex items-center gap-3">
                              <div className="flex h-10 w-10 min-w-[40px] shrink-0 items-center justify-center rounded-[10px] bg-gradient-to-br from-sky-500 via-blue-500 to-blue-700 text-white shadow-[0_6px_16px_-8px_rgba(59,130,246,0.55)]">
                                <Car className="h-[18px] w-[18px] min-h-[18px] min-w-[18px] stroke-[1.9]" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <div
                                  className="truncate font-semibold text-slate-900"
                                  title={veiculo.modelo}
                                >
                                  {veiculo.modelo}
                                </div>
                                <div className="mt-0.5 font-mono text-[12px] tracking-wide text-slate-500 truncate">
                                  {veiculo.placa}
                                </div>
                              </div>
                            </div>
                          </Td>
                          <Td>
                            <span
                              className="font-medium text-slate-700"
                              title={veiculo.marca}
                            >
                              {veiculo.marca}
                            </span>
                          </Td>
                          <Td>
                            <span className="font-semibold tabular-nums text-slate-700">
                              {veiculo.ano}
                            </span>
                          </Td>
                          <Td>
                            <div className="inline-flex items-center gap-1 rounded-md bg-orange-50 px-2.5 py-1 text-[12px] font-bold text-orange-700 ring-1 ring-inset ring-orange-200">
                              <Fuel className="h-3 w-3 stroke-[1.9]" />
                              {veiculo.tipoCombustivel}
                            </div>
                          </Td>
                          <Td>
                            <div className="inline-flex items-center gap-1.5">
                              <Gauge className="h-3.5 w-3.5 stroke-[1.8] text-slate-400" />
                              <span className="font-semibold text-slate-700 tabular-nums">
                                {veiculo.quilometragemAtual.toLocaleString('pt-BR')} km
                              </span>
                            </div>
                          </Td>
                          <Td>
                            <div className="inline-flex items-start gap-1.5">
                              <Building2 className="h-3.5 w-3.5 shrink-0 mt-0.5 stroke-[1.8] text-slate-400" />
                              <span
                                className="block truncate font-medium text-slate-700"
                                title={veiculo.orgaoResponsavel}
                              >
                                {veiculo.orgaoResponsavel}
                              </span>
                            </div>
                          </Td>
                          <Td>{renderStatusBadge(veiculo.status)}</Td>
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </ModernTable>

                {!loading && pageRange.total > 0 && (
                  <div className="mt-5 flex flex-col gap-3 border-t border-slate-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-[13px] font-medium text-slate-500">
                      Mostrando <span className="font-semibold text-slate-700">{pageRange.start}-{pageRange.end}</span> de <span className="font-semibold text-slate-700">{pageRange.total}</span> veículos
                    </div>
                    <div className="inline-flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                        aria-label="Primeira página"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:bg-white disabled:hover:text-slate-500"
                      >
                        <ChevronsLeft className="h-[16px] w-[16px] stroke-[1.8]" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        aria-label="Página anterior"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:bg-white disabled:hover:text-slate-500"
                      >
                        <ChevronLeft className="h-[16px] w-[16px] stroke-[1.8]" />
                      </button>
                      <div className="inline-flex items-center gap-1 mx-1">
                        {getPageNumbers.map((n, idx) =>
                          n === '...' ? (
                            <span
                              key={`dots-${idx}`}
                              className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 text-[13px]"
                            >
                              …
                            </span>
                          ) : (
                            <button
                              key={n}
                              type="button"
                              onClick={() => setCurrentPage(n)}
                              aria-label={`Ir para página ${n}`}
                              className={`inline-flex h-9 min-w-[36px] cursor-pointer items-center justify-center rounded-lg px-2.5 text-[13px] font-semibold transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60 ${
                                n === currentPage
                                  ? 'bg-blue-600 text-white shadow-[0_4px_14px_-4px_rgba(37,99,235,0.55)]'
                                  : 'border border-slate-200 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700'
                              }`}
                            >
                              {n}
                            </button>
                          ),
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        aria-label="Próxima página"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:bg-white disabled:hover:text-slate-500"
                      >
                        <ChevronRight className="h-[16px] w-[16px] stroke-[1.8]" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}
                        aria-label="Última página"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:bg-white disabled:hover:text-slate-500"
                      >
                        <ChevronsRight className="h-[16px] w-[16px] stroke-[1.8]" />
                      </button>
                    </div>
                  </div>
                )}
              </Fragment>
            )}
          </CardBody>
        </SectionCard>

        {isFilterOpen && (
          <FilterOptions
            key={filterModalKey}
            isOpen={isFilterOpen}
            onClose={() => setIsFilterOpen(false)}
            onApplyFilter={handleApplyFilter}
            filterCategories={filterCategories}
            initialFilters={appliedFilters}
          />
        )}
      </div>
    </div>
  );
};

export default CadastroVeiculos;
